import { set } from 'lodash-es';
import { SplitFactory } from '@splitsoftware/splitio';

/**
 * Creates a Split.io client promise that will eventually resolve to a client.
 *
 * @param {string} apiKey - Browser SDK API key
 * @param {Object} splitIdentity - Split identity to attach to the client.
 * @param {Object} logger - Logger instance to use for logging.
 * @returns {{ splitIsReady: Promise<SplitIO.ISDK|undefined> }}
 */
function createBrowserSplitClientPromise(apiKey, splitIdentity, logger) {
    if (!apiKey) {
        logger.debug('[Split] No browser API key provided, Split client disabled.');
        return {
            splitIsReady: Promise.resolve(undefined),
        };
    }

    const splitFactory = SplitFactory({
        core: {
            authorizationKey: apiKey,
            key: splitIdentity?.key,
        },
        scheduler: {
            impressionsRefreshRate: 3,
        },
        userConsent: 'UNKNOWN',
    });

    const splitClient = splitFactory.client();

    const splitIsReady = new Promise((resolve, reject) => {
        splitClient.on(splitClient.Event.SDK_READY, () => resolve({ splitClient, splitFactory }));
        splitClient.on(splitClient.Event.SDK_READY_TIMED_OUT, err => reject(err));
    });

    return splitIsReady;
}

/**
 * Gets a fully resolved Split client ready for immediate use.
 * @param {string} apiKey - Split SDK API key
 * @param {Object} splitIdentity - Split identity to attach to the client
 * @param {boolean} useSplit - whether or not to use the Split sdk
 * @param {Object} logger - Logger instance
 * @returns {Object} Resolved split client & factory or null
 */
export default async function getResolvedSplitClient(apiKey, splitIdentity, useSplit, logger) {
    if (!useSplit) {
        logger.info('\nSplit SDK is disabled\n');
        return null;
    }
    const splitIsReady = createBrowserSplitClientPromise(apiKey, splitIdentity, logger);

    try {
        const { splitClient, splitFactory } = await splitIsReady;
        logger.info('\nSplit SDK is enabled\n');

        // attached to window to be used in GTM
        set(window, 'SplitAnalytics', {
            track(eventName, value, data) {
                splitClient.track(splitIdentity.trafficType, eventName, value, data);
            },
        });

        return { splitClient, splitFactory };
    } catch (err) {
        logger.error('\nIssue Loading Split Client\n', err);
        return null;
    }
}
